/**
 * Ender dragon (vanilla EnderDragon + its phase manager), DOM-free.
 *
 *  - 200 HP, one multi-part hitbox: the head takes full damage, every other part
 *    amount / 4 + 1 (vanilla). `parts()` gives the AABBs, `hurtPart()` applies it.
 *  - Flight: a 24-node path graph (12 outer / 8 middle / 4 inner ring), smooth
 *    turning (the yaw can only change a few hundredths of a radian per tick),
 *    speed and climb eased so the dragon banks through the rings.
 *  - Phases: hold (circle), strafe (fireball run), charge (swoop at the player),
 *    approach -> landing -> sit_scan / sit_attack (roar) / sit_flame (breath) on the
 *    exit fountain -> takeoff, and dying (ascend while the beam builds).
 *  - Perch odds climb as end crystals fall; with none left it only uses the
 *    middle ring and perches as soon as its cooldown allows (vanilla).
 *
 * Everything the world must do (block removal, damage, fireballs) goes through
 * `DragonEnv`, so the smoke test drives the same class headless.
 */

import { approachAngle, wrapAngle, yawTo } from '../host';

export type DragonPhase =
  | 'hold' | 'strafe' | 'charge' | 'approach' | 'landing'
  | 'sit_scan' | 'sit_attack' | 'sit_flame' | 'takeoff' | 'dying';

export type PartId = 'head' | 'neck1' | 'neck2' | 'neck3' | 'body' | 'tail1' | 'tail2' | 'tail3' | 'wing_l' | 'wing_r';

/** [width, height] of each part's hitbox (vanilla EnderDragon sub-entities). */
export const PART_SIZE: Record<PartId, readonly [number, number]> = {
  head: [1, 1], neck1: [3, 3], neck2: [3, 3], neck3: [3, 3], body: [5, 3],
  tail1: [2, 2], tail2: [2, 2], tail3: [2, 2], wing_l: [4, 2], wing_r: [4, 2],
};

export const DRAGON_MAX_HEALTH = 200;
/** Damage taken while perched that makes it take off again. */
export const PERCH_LEAVE_DAMAGE = 50;
export const XP_FIRST_KILL = 12000;
export const XP_REPEAT_KILL = 500;
export const DEATH_TICKS = 200;

export interface DragonPart { id: PartId; x: number; y: number; z: number; w: number; h: number }
/** One chain link of the neck / tail (shared by the hitboxes and the model). */
export interface ChainLink { x: number; y: number; z: number; yaw: number; pitch: number }

export type HurtKind = 'player' | 'explosion' | 'projectile' | 'other';

export interface EndLayout {
  pillars: readonly { x: number; z: number; height: number; radius: number; caged: boolean }[];
  /** Exit fountain; y is the portal's base (the dragon perches just above the 4-tall stem). */
  exit: { x: number; y: number; z: number };
}

export interface DragonEnv {
  rnd(): number;
  crystalsAlive(): number;
  /** Nearest living end crystal within `range` blocks of the point, for healing. */
  nearestCrystal(x: number, y: number, z: number, range: number): { id: number } | null;
  player(): { x: number; y: number; z: number; eyeY: number; alive: boolean; targetable: boolean } | null;
  /** True when the dragon's head can see the point (fireball LOS). */
  los(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean;
  shootFireball(x: number, y: number, z: number, dx: number, dy: number, dz: number): void;
  /** Breath cloud (radius, duration in ticks) at a point. */
  breathCloud(x: number, y: number, z: number, radius: number, duration: number): void;
  /** Dragon body part touched the player: damage + knockback away from (x, z). */
  hitPlayer(amount: number, fromX: number, fromZ: number, knock: number): void;
  /** Remove every non-immune block overlapping the box. */
  smash(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void;
  event(kind: 'roar' | 'hurt' | 'flap' | 'growl' | 'death_blast', x: number, y: number, z: number): void;
  dropXp(amount: number, x: number, y: number, z: number): void;
  /** Death animation finished: the fight activates the exit portal. */
  onDeath(): void;
}

const NODE_COUNT = 24;
const HIST = 64;

export class EnderDragon {
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx = 0; vy = 0; vz = 0;
  yaw = 0; pyaw = 0; pitch = 0; ppitch = 0;
  health = DRAGON_MAX_HEALTH;
  readonly maxHealth = DRAGON_MAX_HEALTH;
  phase: DragonPhase = 'hold';
  phaseTicks = 0;
  /** Ticks since the last hurt (for the renderer flash). */
  hurtTime = 0;
  hurtResist = 0;
  private lastDamage = 0;
  deathTime = 0;
  /** Wing flap angle (radians) and its previous-tick value for interpolation. */
  flap = 0; pflap = 0;
  /** Damage taken in the current perch (leave at PERCH_LEAVE_DAMAGE). */
  perchDamage = 0;
  perchTicks = 0;
  /** Ticks until the dragon may land again. */
  perchCooldown = 600;
  /** True after the first ever kill (set by the fight; 500 XP instead of 12000). */
  repeatKill = false;
  /** 0..1 head open amount for the renderer (roar / breath). */
  mouth = 0;
  /** Index (0..23) of the node it is flying toward. */
  node = 0;
  readonly nodes: Array<[number, number, number]> = [];
  removed = false;
  removedAfterDeath = false;

  private clockwise = true;
  private nodeTicks = 0;
  private target: [number, number, number] = [0, 0, 0];
  private fireballCharge = 0;
  private fired = false;
  private hitCd = 0;
  private xpLeft = 0;
  private yawHist = new Float32Array(HIST);
  private yHist = new Float32Array(HIST);
  private histPos = 0;
  private readonly env: DragonEnv;
  private readonly layout: EndLayout;
  private healTimer = 0;
  private flapSound = 0;

  constructor(env: DragonEnv, layout: EndLayout, x: number, y: number, z: number) {
    this.env = env;
    this.layout = layout;
    this.x = this.px = x; this.y = this.py = y; this.z = this.pz = z;
    this.buildNodes();
    this.node = this.closestNode();
    this.yawHist.fill(this.yaw); this.yHist.fill(y);
    this.target = [...this.nodes[this.node]];
  }

  /** Y the body sits at while perched (above the fountain top). */
  get perchY(): number { return this.layout.exit.y + 5; }

  get perched(): boolean {
    return this.phase === 'sit_scan' || this.phase === 'sit_attack' || this.phase === 'sit_flame';
  }
  get dying(): boolean { return this.phase === 'dying'; }
  get alive(): boolean { return this.phase !== 'dying' && !this.removed; }

  /** Vanilla layout: 12 nodes at r=60, 8 at r=40, 4 at r=20, heights varied above the island. */
  private buildNodes(): void {
    const e = this.layout.exit;
    const ring = (n: number, r: number, base: number, amp: number, phase: number): void => {
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + phase;
        this.nodes.push([e.x + Math.cos(a) * r, e.y + base + Math.sin(a * 2 + phase) * amp, e.z + Math.sin(a) * r]);
      }
    };
    ring(12, 60, 34, 8, 0);
    ring(8, 40, 28, 6, 0.3);
    ring(4, 20, 22, 4, 0.7);
  }

  private closestNode(): number {
    let best = 0, bd = Infinity;
    for (let i = 0; i < NODE_COUNT; i++) {
      const n = this.nodes[i];
      const d = (n[0] - this.x) ** 2 + (n[1] - this.y) ** 2 + (n[2] - this.z) ** 2;
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  /** Next node along the rings: keeps its direction, sometimes reverses or hops ring. */
  private nextNode(): number {
    const r = this.env.rnd;
    const none = this.env.crystalsAlive() <= 0;
    let i = this.node;
    const ringStart = (n: number): number => (n < 12 ? 0 : n < 20 ? 12 : 20);
    const ringLen = (n: number): number => (n < 12 ? 12 : n < 20 ? 8 : 4);
    if (r() < 0.125) this.clockwise = !this.clockwise;
    let base = ringStart(i), len = ringLen(i);
    // hop to the neighbouring ring now and then (middle only once the crystals are gone)
    if (none && base !== 12) { i = 12 + Math.floor(((i - base) / len) * 8) % 8; base = 12; len = 8; }
    else if (r() < 0.2) {
      const nb = base === 12 ? (r() < 0.5 ? 0 : 20) : 12;
      const nl = ringLen(nb);
      i = nb + Math.floor(((i - base) / len) * nl) % nl;
      base = nb; len = nl;
    }
    const k = (i - base + (this.clockwise ? 1 : len - 1)) % len;
    return base + k;
  }

  setPhase(p: DragonPhase): void {
    this.phase = p;
    this.phaseTicks = 0;
    this.fired = false;
    this.fireballCharge = 0;
  }

  // --- hitboxes --------------------------------------------------------------

  private histAt(arr: Float32Array, lag: number): number {
    const l = Math.max(0, Math.min(HIST - 1, Math.round(lag)));
    return arr[(this.histPos - l + HIST * 2) % HIST];
  }

  private static dirOf(yaw: number, pitch: number, out: [number, number, number]): void {
    const c = Math.cos(pitch);
    out[0] = -Math.sin(yaw) * c; out[1] = Math.sin(pitch); out[2] = -Math.cos(yaw) * c;
  }

  /** Neck links (5, from the shoulder to the base of the head). Interpolated by `a`. */
  neckChain(a = 1): ChainLink[] {
    const out: ChainLink[] = [];
    const yaw = this.pyaw + wrapAngle(this.yaw - this.pyaw) * a;
    const bp = this.ppitch + (this.pitch - this.ppitch) * a;
    const x = this.px + (this.x - this.px) * a, y = this.py + (this.y - this.py) * a, z = this.pz + (this.z - this.pz) * a;
    const d: [number, number, number] = [0, 0, 0];
    EnderDragon.dirOf(yaw, bp, d);
    let cx = x + d[0] * 3.6, cy = y + 1.2, cz = z + d[2] * 3.6;
    const lift = this.perched ? 0.55 : 0.28;
    for (let i = 0; i < 5; i++) {
      const sway = Math.sin(this.age * 0.05 - i * 0.5) * 0.06;
      const ly = yaw + sway;
      const lp = bp + lift * (i / 4) + (this.perched ? 0.1 : 0);
      out.push({ x: cx, y: cy, z: cz, yaw: ly, pitch: lp });
      EnderDragon.dirOf(ly, lp, d);
      cx += d[0] * 1.25; cy += d[1] * 1.25; cz += d[2] * 1.25;
    }
    return out;
  }

  /** Tail links (12, from the hips back), curving along where the body has been. */
  tailChain(a = 1): ChainLink[] {
    const out: ChainLink[] = [];
    const yaw = this.pyaw + wrapAngle(this.yaw - this.pyaw) * a;
    const bp = this.ppitch + (this.pitch - this.ppitch) * a;
    let cx = this.px + (this.x - this.px) * a, cy = this.py + (this.y - this.py) * a, cz = this.pz + (this.z - this.pz) * a;
    const d: [number, number, number] = [0, 0, 0];
    EnderDragon.dirOf(yaw, bp, d);
    cx -= d[0] * 3.8; cy -= d[1] * 3.8 - 0.4; cz -= d[2] * 3.8;
    for (let i = 0; i < 12; i++) {
      const lag = 4 + i * 3;
      // the tail trails the body: past yaw / height bend the chain
      const ly = this.histAt(this.yawHist, lag);
      const dy = this.histAt(this.yHist, lag) - this.histAt(this.yHist, lag + 4);
      const lp = Math.max(-0.7, Math.min(0.7, Math.atan2(dy, 3)));
      const wag = Math.sin(this.age * 0.08 - i * 0.45) * 0.05 * (i + 1) * 0.3;
      out.push({ x: cx, y: cy, z: cz, yaw: ly + wag, pitch: lp });
      EnderDragon.dirOf(ly + wag, lp, d);
      cx -= d[0] * 1.3; cy -= d[1] * 1.3; cz -= d[2] * 1.3;
    }
    return out;
  }

  /** Head position / yaw / pitch (end of the neck, looking along `headYaw`). */
  head(a = 1): ChainLink {
    const neck = this.neckChain(a);
    const l = neck[4];
    const d: [number, number, number] = [0, 0, 0];
    EnderDragon.dirOf(l.yaw, l.pitch, d);
    const down = this.perched ? -0.35 : -0.15;
    return { x: l.x + d[0] * 2.0, y: l.y + d[1] * 2.0, z: l.z + d[2] * 2.0, yaw: l.yaw, pitch: l.pitch + down };
  }

  /** AABB centres of the ten hitbox parts. */
  parts(): DragonPart[] {
    const mk = (id: PartId, x: number, y: number, z: number): DragonPart => ({ id, x, y, z, w: PART_SIZE[id][0], h: PART_SIZE[id][1] });
    const neck = this.neckChain();
    const tail = this.tailChain();
    const h = this.head();
    const rx = -Math.cos(this.yaw), rz = Math.sin(this.yaw); // right vector for forward (-sin, -cos)
    const wy = Math.sin(this.flap) * 1.4;
    return [
      mk('head', h.x, h.y, h.z),
      mk('neck1', neck[4].x, neck[4].y, neck[4].z),
      mk('neck2', neck[2].x, neck[2].y, neck[2].z),
      mk('neck3', neck[0].x, neck[0].y, neck[0].z),
      mk('body', this.x, this.y + 1.5, this.z),
      mk('tail1', tail[2].x, tail[2].y, tail[2].z),
      mk('tail2', tail[6].x, tail[6].y, tail[6].z),
      mk('tail3', tail[10].x, tail[10].y, tail[10].z),
      mk('wing_l', this.x - rx * 6.2, this.y + 2 + wy, this.z - rz * 6.2),
      mk('wing_r', this.x + rx * 6.2, this.y + 2 + wy, this.z + rz * 6.2),
    ];
  }

  age = 0;

  // --- damage ----------------------------------------------------------------

  /** Vanilla: the head takes full damage, everything else amount / 4 + 1. */
  static partDamage(part: PartId, amount: number): number {
    return part === 'head' ? amount : amount / 4 + 1;
  }

  /**
   * Hit a part. Returns the HP actually removed (0 when deflected / absent).
   * Projectiles bounce off while it perches; hits inside the invulnerability
   * window only count their excess (vanilla LivingEntity.hurt).
   */
  hurtPart(part: PartId, amount: number, kind: HurtKind): number {
    if (!this.alive) return 0;
    if (kind === 'projectile' && (this.perched || this.phase === 'landing')) return 0;
    let dmg = EnderDragon.partDamage(part, amount);
    if (this.hurtResist > 0) {
      if (dmg <= this.lastDamage) return 0;
      const extra = dmg - this.lastDamage;
      this.lastDamage = dmg;
      dmg = extra;
    } else {
      this.lastDamage = dmg;
      this.hurtResist = 10;
    }
    dmg = Math.min(dmg, this.health);
    this.health -= dmg;
    this.hurtTime = 10;
    this.env.event('hurt', this.x, this.y + 2, this.z);
    if (this.perched) {
      this.perchDamage += dmg;
      if (this.perchDamage >= PERCH_LEAVE_DAMAGE) this.setPhase('takeoff');
    }
    if (this.health <= 0.0001) { this.health = 0; this.startDying(); }
    return dmg;
  }

  /** Crystal destroyed while it healed us: vanilla hurts the head for 10 and may retaliate. */
  onCrystalDestroyed(byPlayer: boolean, wasHealing: boolean): number {
    let dealt = 0;
    if (wasHealing) { this.hurtResist = 0; dealt = this.hurtPart('head', 10, 'explosion'); }
    if (this.alive && byPlayer && this.phase === 'hold') {
      const p = this.env.player();
      if (p && p.targetable) this.setPhase('strafe');
    }
    return dealt;
  }

  private startDying(): void {
    this.phase = 'dying';
    this.phaseTicks = 0;
    this.deathTime = 0;
    this.vx = this.vz = 0;
    this.xpLeft = this.repeatKill ? XP_REPEAT_KILL : XP_FIRST_KILL;
  }

  // --- tick ------------------------------------------------------------------

  tick(): void {
    this.px = this.x; this.py = this.y; this.pz = this.z;
    this.pyaw = this.yaw; this.ppitch = this.pitch; this.pflap = this.flap;
    this.age++;
    if (this.hurtResist > 0) this.hurtResist--;
    if (this.hurtTime > 0) this.hurtTime--;
    if (this.hitCd > 0) this.hitCd--;
    if (this.perchCooldown > 0 && !this.perched && this.phase !== 'landing' && this.phase !== 'approach') this.perchCooldown--;
    this.phaseTicks++;

    if (this.phase === 'dying') { this.tickDying(); this.pushHistory(); return; }

    this.heal();
    this.tickPhase();
    this.integrate();
    this.animate();
    this.pushHistory();
    this.collide();
  }

  /**
   * A replica of the server's dragon (client, server mode): take one tick of its
   * state instead of running tick(). Keeps the trail the tail bends along.
   */
  follow(x: number, y: number, z: number, yaw: number, flap: number, hurtTime: number, deathTime: number): void {
    this.px = this.x; this.py = this.y; this.pz = this.z; this.pyaw = this.yaw; this.pflap = this.flap; this.ppitch = this.pitch;
    this.x = x; this.y = y; this.z = z; this.yaw = yaw; this.flap = flap;
    this.hurtTime = hurtTime; this.deathTime = deathTime;
    this.phase = deathTime > 0 ? 'dying' : 'hold';
    this.age++;
    this.pushHistory();
  }

  private pushHistory(): void {
    this.histPos = (this.histPos + 1) % HIST;
    this.yawHist[this.histPos] = this.yaw;
    this.yHist[this.histPos] = this.y;
  }

  /** +1 HP every 10 ticks while a crystal within 32 blocks beams at it. */
  private heal(): void {
    if (++this.healTimer < 10) return;
    this.healTimer = 0;
    if (this.health < this.maxHealth && this.env.nearestCrystal(this.x, this.y, this.z, 32)) {
      this.health = Math.min(this.maxHealth, this.health + 1);
    }
  }

  private tickDying(): void {
    const t = ++this.deathTime;
    this.y += 0.1;
    this.vy = 0.1;
    this.flap += 0.1;
    this.mouth = 1;
    // shaking head / bursts of light while it rises
    if (t % 4 === 0) this.env.event('growl', this.x, this.y + 2, this.z);
    // vanilla: 8% of the XP every 5 ticks from tick 151, the last 20% at the end
    if (t > 150 && t % 5 === 0 && t < DEATH_TICKS) {
      const n = Math.floor(this.xpTotal() * 0.08);
      this.xpLeft -= n;
      this.env.dropXp(n, this.x, this.y, this.z);
    }
    if (t === DEATH_TICKS) {
      this.env.dropXp(Math.max(0, this.xpLeft), this.x, this.y, this.z);
      this.xpLeft = 0;
      this.env.event('death_blast', this.x, this.y + 2, this.z);
      this.removed = true;
      this.removedAfterDeath = true;
      this.env.onDeath();
    }
  }

  private xpTotal(): number { return this.repeatKill ? XP_REPEAT_KILL : XP_FIRST_KILL; }

  // --- phases ------------------------------------------------------------------

  private tickPhase(): void {
    const p = this.env.player();
    const live = !!p && p.alive && p.targetable;
    switch (this.phase) {
      case 'hold': {
        const n = this.nodes[this.node];
        this.target = [n[0], n[1], n[2]];
        this.nodeTicks++;
        const d = Math.hypot(n[0] - this.x, n[2] - this.z);
        if (d < 16 && Math.abs(n[1] - this.y) < 14 || this.nodeTicks > 300) this.arrive(live);
        this.fly(0.8, 0.05);
        break;
      }
      case 'strafe': {
        if (!p || !live) { this.setPhase('hold'); break; }
        // fly at the player, loose a fireball when lined up, then break off
        this.target = [p.x, p.y + 10, p.z];
        const dx = p.x - this.x, dz = p.z - this.z;
        const dist = Math.hypot(dx, dz);
        const want = yawTo(dx, dz);
        const lined = Math.abs(wrapAngle(want - this.yaw)) < 0.5;
        if (!this.fired && lined && dist < 64 && dist > 8 &&
          this.env.los(this.x, this.y + 2, this.z, p.x, p.eyeY, p.z)) {
          if (++this.fireballCharge >= 5) {
            this.fireballCharge = 0;
            this.fired = true;
            const h = this.head();
            const fx = p.x - h.x, fy = p.y + 0.9 - h.y, fz = p.z - h.z;
            const l = Math.hypot(fx, fy, fz) || 1;
            this.env.shootFireball(h.x, h.y, h.z, fx / l, fy / l, fz / l);
            this.mouth = 1;
          }
        }
        if (this.fired && this.phaseTicks > 12 && dist < 36 || this.phaseTicks > 200 || (dist < 6 && !this.fired)) {
          this.node = this.closestNode();
          this.setPhase('hold');
        }
        this.fly(0.85, 0.07);
        break;
      }
      case 'charge': {
        // lunge at where the player stood when the swoop began (vanilla locks the point)
        const dx = this.target[0] - this.x, dz = this.target[2] - this.z;
        const dist = Math.hypot(dx, this.target[1] - this.y, dz);
        const overshoot = (dx * -Math.sin(this.yaw) + dz * -Math.cos(this.yaw)) < 0;
        if (dist < 5 || overshoot && this.phaseTicks > 10 || this.phaseTicks > 110) {
          this.node = this.closestNode();
          this.setPhase('hold');
          this.perchCooldown = Math.max(this.perchCooldown, 80);
        }
        this.fly(1.15, 0.09);
        break;
      }
      case 'approach': {
        const e = this.layout.exit;
        this.target = [e.x, this.perchY + 22, e.z];
        const d = Math.hypot(e.x - this.x, e.z - this.z);
        if (d < 22 && Math.abs(this.y - (this.perchY + 22)) < 10 || this.phaseTicks > 360) this.setPhase('landing');
        this.fly(0.7, 0.06);
        break;
      }
      case 'landing': {
        const e = this.layout.exit;
        const dx = e.x - this.x, dz = e.z - this.z, dy = this.perchY - this.y;
        const hd = Math.hypot(dx, dz);
        const sp = Math.min(0.5, 0.06 + hd * 0.05);
        // glide straight in, turning to face away from the fountain's centre
        const want = hd > 3 ? yawTo(dx, dz) : this.yaw;
        this.yaw = approachAngle(this.yaw, want, 0.06);
        const k = hd > 0.01 ? sp / hd : 0;
        this.vx = dx * k; this.vz = dz * k;
        this.vy += (Math.max(-0.35, Math.min(0.35, dy * 0.12)) - this.vy) * 0.2;
        if (hd < 1.5 && Math.abs(dy) < 1.2) {
          this.perchDamage = 0;
          this.perchTicks = 1;
          this.vx = this.vz = this.vy = 0;
          this.setPhase('sit_scan');
          this.perchTicks = 1;
        }
        if (this.phaseTicks > 500) this.setPhase('takeoff');
        break;
      }
      case 'sit_scan': {
        this.sitFace(p, live);
        // vanilla: look for a player; close enough to bite -> roar, else breathe after a while
        const near = live && Math.hypot(p!.x - this.x, p!.z - this.z) < 22;
        if (near && this.phaseTicks > 20 || this.phaseTicks > 100) this.setPhase('sit_attack');
        break;
      }
      case 'sit_attack': {
        this.sitFace(p, live);
        this.mouth = Math.min(1, this.phaseTicks / 10);
        if (this.phaseTicks === 5) this.env.event('roar', this.x, this.y + 2, this.z);
        if (this.phaseTicks === 20 && live && Math.hypot(p!.x - this.x, p!.z - this.z) < 10) {
          this.env.hitPlayer(0, this.x, this.z, 1.2);
        }
        if (this.phaseTicks >= 40) this.setPhase('sit_flame');
        break;
      }
      case 'sit_flame': {
        this.sitFace(p, live);
        this.mouth = 1;
        if (this.phaseTicks % 20 === 5) {
          const h = this.head();
          // pours breath toward the player (or just ahead of the head when nobody is close)
          const near = live && Math.hypot(p!.x - this.x, p!.z - this.z) < 24;
          const tx = near ? p!.x : h.x, ty = near ? p!.y : this.layout.exit.y + 1, tz = near ? p!.z : h.z;
          this.env.breathCloud(tx, ty, tz, 4, 200);
        }
        if (this.phaseTicks >= 200) this.setPhase(this.perchTicks > 450 ? 'takeoff' : 'sit_scan');
        break;
      }
      case 'takeoff': {
        const e = this.layout.exit;
        this.target = [e.x + 30, this.perchY + 36, e.z];
        this.mouth = 0;
        this.vy += (0.35 - this.vy) * 0.08;
        const sp = Math.min(0.75, 0.1 + this.phaseTicks * 0.012);
        this.yaw = approachAngle(this.yaw, yawTo(this.target[0] - this.x, this.target[2] - this.z), 0.04);
        this.vx = -Math.sin(this.yaw) * sp; this.vz = -Math.cos(this.yaw) * sp;
        if (this.y > this.perchY + 18) {
          this.node = this.closestNode();
          this.setPhase('hold');
          this.perchCooldown = 400 + this.env.crystalsAlive() * 80;
          this.perchTicks = 0;
        }
        break;
      }
      default: break;
    }
    if (this.perched) this.perchTicks++;
    if (this.phase !== 'sit_attack' && this.phase !== 'sit_flame' && this.phase !== 'dying') {
      this.mouth = Math.max(0, this.mouth - 0.08);
    }
    if (this.phase === 'sit_scan') this.mouth = Math.max(0, this.mouth - 0.1);
  }

  /** Perched: face the player if there is one, else stay put. */
  private sitFace(p: ReturnType<DragonEnv['player']>, live: boolean): void {
    if (live) this.yaw = approachAngle(this.yaw, yawTo(p!.x - this.x, p!.z - this.z), 0.05);
    this.vx = this.vy = this.vz = 0;
  }

  /** Arrived at the hold node: pick the next move (strafe / charge / perch / next node). */
  private arrive(live: boolean): void {
    const r = this.env.rnd;
    const c = this.env.crystalsAlive();
    this.nodeTicks = 0;
    const p = this.env.player();
    if (live && p) {
      const d = Math.hypot(p.x - this.x, p.z - this.z);
      if (d < 70 && r() * (c + 3) < 1) { this.setPhase('strafe'); return; }
      if (d < 45 && r() < 0.14) {
        this.target = [p.x, p.y + 1, p.z];
        this.setPhase('charge');
        return;
      }
    }
    if (this.perchCooldown <= 0 && r() * (c + 2) < 1) { this.setPhase('approach'); return; }
    this.node = this.nextNode();
  }

  // --- movement ---------------------------------------------------------------

  /** Turn toward the target at a limited rate, hold speed, ease the climb. */
  private fly(speed: number, turn: number): void {
    const [tx, ty, tz] = this.target;
    const dx = tx - this.x, dz = tz - this.z;
    const hd = Math.hypot(dx, dz);
    if (hd > 0.5) this.yaw = approachAngle(this.yaw, yawTo(dx, dz), turn);
    // slow a little when the point is behind us so it swings wide instead of stalling
    const off = Math.abs(wrapAngle(yawTo(dx, dz) - this.yaw));
    const sp = speed * (off > 1.2 ? 0.7 : 1);
    this.vx += (-Math.sin(this.yaw) * sp - this.vx) * 0.25;
    this.vz += (-Math.cos(this.yaw) * sp - this.vz) * 0.25;
    const climb = Math.max(-0.45, Math.min(0.45, (ty - this.y) * 0.08));
    this.vy += (climb - this.vy) * 0.15;
  }

  private integrate(): void {
    this.x += this.vx; this.y += this.vy; this.z += this.vz;
    const hs = Math.hypot(this.vx, this.vz);
    const want = Math.max(-0.6, Math.min(0.6, Math.atan2(this.vy, Math.max(hs, 0.2)) * 1.4));
    this.pitch += (want - this.pitch) * 0.15;
  }

  private animate(): void {
    const climbing = this.vy > 0.05;
    const rate = this.perched ? 0.12 : climbing ? 0.34 : 0.26;
    this.flap += rate;
    if (!this.perched && Math.sin(this.flap) < -0.9 && Math.sin(this.pflap) >= -0.9 && ++this.flapSound >= 2) {
      this.flapSound = 0;
      this.env.event('flap', this.x, this.y, this.z);
    }
  }

  /** Melee: head 10, wings 5 (+ knockback); body parts shove the player. */
  private collide(): void {
    const parts = this.parts();
    // destroy blocks the whole body passes through (not while it sits on the fountain)
    if (!this.perched) {
      for (const p of parts) {
        if (p.id.startsWith('tail')) continue;
        const hw = p.w / 2 + 0.25;
        this.env.smash(p.x - hw, p.y - p.h / 2 - 0.25, p.z - hw, p.x + hw, p.y + p.h / 2 + 0.25, p.z + hw);
      }
    }
    const pl = this.env.player();
    if (!pl || !pl.alive || !pl.targetable || this.hitCd > 0 || this.perched) return;
    for (const p of parts) {
      const hw = p.w / 2 + 0.3;
      if (Math.abs(pl.x - p.x) > hw || Math.abs(pl.z - p.z) > hw || pl.y > p.y + p.h / 2 + 0.2 || pl.y + 1.8 < p.y - p.h / 2) continue;
      if (p.id === 'head') { this.hitCd = 10; this.env.hitPlayer(10, this.x, this.z, 1.0); return; }
      if (p.id === 'wing_l' || p.id === 'wing_r') {
        // swatted aside: 5 damage and a big shove away from the body
        this.hitCd = 10; this.env.hitPlayer(5, p.x - (p.x - this.x) * 0.4, p.z - (p.z - this.z) * 0.4, 2.2); return;
      }
    }
  }
}
